import React, { createContext, useContext, useEffect, useState } from "react";
import secureStorage from "../utils/secureStorage";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

// ✅ Always default to LIGHT unless user explicitly sets dark
const getInitialTheme = () => {
  const savedTheme = secureStorage.getItem("vibelinks-theme");
  const isDark = savedTheme === "dark"; // only true if user chose dark manually

  if (isDark) {
    document.documentElement.classList.add("dark");
  } else {
    document.documentElement.classList.remove("dark");
  }

  return isDark;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => getInitialTheme());

  // ✅ Sync with stored preference only (no system detection)
  useEffect(() => {
    const savedTheme = secureStorage.getItem("vibelinks-theme");

    if (savedTheme === "dark") {
      setIsDarkMode(true);
      document.documentElement.classList.add("dark");
    } else {
      setIsDarkMode(false);
      document.documentElement.classList.remove("dark");
    }
  }, []);

  // ✅ Toggle function (save preference)
  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);

    if (newTheme) {
      document.documentElement.classList.add("dark");
      secureStorage.setItem("vibelinks-theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      secureStorage.setItem("vibelinks-theme", "light");
    }
  };

  // ✅ Direct setter (for programmatic theme switch)
  const setTheme = (dark) => {
    setIsDarkMode(dark);
    if (dark) {
      document.documentElement.classList.add("dark");
      secureStorage.setItem("vibelinks-theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      secureStorage.setItem("vibelinks-theme", "light");
    }
  };

  const value = {
    isDarkMode,
    toggleTheme,
    setTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
